import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Store, Truck, Package, Receipt, TrendingUp, AlertTriangle, RefreshCw, ArrowRight, IndianRupee, Lock, FileText, CheckCircle2, Download, Printer } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/utils-format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import BranchTeamPanel from "@/components/hr/BranchTeamPanel";

/**
 * StoreDashboardPage — the landing page for showroom_admin & showroom_sales_manager.
 * Shows only their assigned store's snapshot: POS today, pending GRN, low stock,
 * recent dispatches inbound.
 */
export default function StoreDashboardPage() {
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [pending, setPending] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [todaySales, setTodaySales] = useState({ count: 0, amount: 0 });
  const [loading, setLoading] = useState(false);
  const [eodSnapshot, setEodSnapshot] = useState(null);
  const [eodHistory, setEodHistory] = useState([]);
  const [eodDialogOpen, setEodDialogOpen] = useState(false);
  const [skuRows, setSkuRows] = useState([]);
  const [eodBills, setEodBills] = useState(null);

  const load = async () => {
    if (!user?.branch_id) return;
    setLoading(true);
    try {
      const [b, grnPend, disp, eodToday, eodHist, sku, bills] = await Promise.all([
        api.get(`/branches`).then((r) => (r.data || []).find((x) => x.id === user.branch_id)),
        api.get(`/grn?status=draft`).catch(() => ({ data: [] })),
        api.get(`/grn/inbound`).catch(() => ({ data: [] })),
        api.get(`/pos/eod/today`).catch(() => ({ data: null })),
        api.get(`/pos/eod?limit=7`).catch(() => ({ data: [] })),
        api.get(`/ho/sku-report?branch_id=${user.branch_id}`).catch(() => ({ data: { items: [] } })),
        api.get(`/pos/eod/bills`).catch(() => ({ data: null })),
      ]);
      setStore(b);
      setPending(grnPend.data || []);
      setInbound(disp.data || []);
      setEodSnapshot(eodToday.data);
      setEodHistory(eodHist.data || []);
      setSkuRows(sku.data?.items || []);
      setEodBills(bills.data);
      if (eodToday.data) {
        setTodaySales({
          count: eodToday.data.bill_count || 0,
          amount: eodToday.data.net_sales || 0,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load store dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.branch_id]);

  return (
    <div className="space-y-6" data-testid="store-dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 uc-label">
            <Store className="w-3.5 h-3.5" /> Retail · Store view
            {store?.store_code && (
              <Badge className="border-0 bg-[#C05621] text-white font-mono">{store.store_code}</Badge>
            )}
          </div>
          <h1 className="font-[Outfit] text-2xl font-semibold text-[#2C1810] mt-1">
            {store?.name || "My Store"}
          </h1>
          <p className="text-sm text-[#8B7355] mt-1">
            Scoped to your assigned store — POS, Stock and Goods Received Notes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {eodSnapshot?.already_closed ? (
            <Badge className="bg-red-100 border border-red-200 text-red-800 font-medium" data-testid="store-eod-locked">
              <Lock className="w-3 h-3 mr-1" /> POS locked · EOD closed
            </Badge>
          ) : (
            <Button
              size="sm"
              onClick={() => setEodDialogOpen(true)}
              data-testid="store-eod-btn"
              className="bg-[#2C1810] hover:bg-black text-white"
            >
              <Lock className="w-4 h-4 mr-1.5" /> End-of-Day Close
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="store-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          testid="store-kpi-inbound"
          label="Inbound dispatches"
          value={inbound.length}
          icon={Truck}
          tone="blue"
          hint={inbound.length === 0 ? "No pending deliveries" : "Awaiting GRN"}
        />
        <Kpi
          testid="store-kpi-drafts"
          label="Draft GRNs"
          value={pending.length}
          icon={Package}
          tone="amber"
          hint={pending.length === 0 ? "Nothing pending" : "Finalise to update stock"}
        />
        <Kpi
          testid="store-kpi-sales-today"
          label="Bills today"
          value={todaySales.count}
          icon={Receipt}
          tone="emerald"
          hint="Today's POS receipts"
        />
        <Kpi
          testid="store-kpi-revenue-today"
          label="Today's revenue"
          value={`₹${Number(todaySales.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          icon={IndianRupee}
          tone="emerald"
          hint="Sum of POS bills"
        />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <QuickCard
          to="/app/pos"
          title="Open POS"
          desc="Bill customers, apply loyalty, print receipts"
          icon={Receipt}
          testid="store-quick-pos"
        />
        <QuickCard
          to="/app/grn"
          title="Goods Received Notes"
          desc="Receive inbound stock from warehouse"
          icon={Package}
          testid="store-quick-grn"
        />
        <QuickCard
          to="/app/branches"
          title="Local Stock"
          desc="Live stock levels for your store"
          icon={TrendingUp}
          testid="store-quick-stock"
        />
      </div>

      {/* End of Day Summary — bills grouped by payment method */}
      <EodSummary data={eodBills} store={store} user={user} />

      {/* Inbound dispatches table */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#8B7355] mb-2 flex items-center gap-1.5">
          <Truck className="w-3.5 h-3.5" /> Pending inbound (last 5)
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uc-label border-b border-[#EAE5D9]">
                <th className="px-3 py-2">Number</th>
                <th className="px-3 py-2">From</th>
                <th className="px-3 py-2">Driver / Vehicle</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {inbound.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-[#8B7355]">
                    No pending dispatches. All caught up.
                  </td>
                </tr>
              )}
              {inbound.slice(0, 5).map((d) => (
                <tr key={d.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`store-inbound-${d.id}`}>
                  <td className="px-3 py-2 font-mono text-[12px] font-medium">{d.number}</td>
                  <td className="px-3 py-2">{d.from_branch_name}</td>
                  <td className="px-3 py-2 text-[12px]">
                    {d.driver_name || "—"} · {d.vehicle_no || "no vehicle"}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className="border bg-blue-50 border-blue-200 text-blue-800">{d.current_status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to="/app/grn">
                      <Button size="sm" variant="outline" className="text-[11px]">
                        Receive <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* EOD history */}
      {eodHistory.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#8B7355] mb-2 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Recent EOD closes
          </div>
          <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left uc-label border-b border-[#EAE5D9]">
                  <th className="px-3 py-2">Close #</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2 text-right">Net Sales</th>
                  <th className="px-3 py-2 text-right">Bills</th>
                  <th className="px-3 py-2 text-right">Variance</th>
                  <th className="px-3 py-2">Signed by</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {eodHistory.map((h) => (
                  <tr key={h.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`store-eod-hist-${h.id}`}>
                    <td className="px-3 py-2 font-mono text-[12px] font-medium">{h.number}</td>
                    <td className="px-3 py-2">{h.business_date}</td>
                    <td className="px-3 py-2 text-right font-mono">₹{Number(h.snapshot?.net_sales || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="px-3 py-2 text-right">{h.snapshot?.bill_count || 0}</td>
                    <td className={`px-3 py-2 text-right font-mono ${Math.abs(h.total_variance || 0) > 0.5 ? "text-red-600" : "text-emerald-700"}`}>
                      {h.total_variance > 0 ? "+" : ""}₹{Number(h.total_variance || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-[11px]">{h.signature_name}</td>
                    <td className="px-3 py-2 text-right">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/pos/eod/${h.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={async (e) => {
                          e.preventDefault();
                          const token = localStorage.getItem("token");
                          const r = await fetch(e.target.closest("a").href, { headers: { Authorization: `Bearer ${token}` } });
                          const blob = await r.blob();
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement("a");
                          link.href = url; link.download = `EOD_${h.number.replace(/\//g, "_")}.pdf`;
                          link.click(); URL.revokeObjectURL(url);
                        }}
                        data-testid={`store-eod-pdf-${h.id}`}
                        className="text-xs text-[#C05621] hover:underline inline-flex items-center gap-1"
                      >
                        <Download className="w-3.5 h-3.5" /> PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SKU-wise stock at this store */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#8B7355] mb-2 flex items-center gap-1.5">
          <Package className="w-3.5 h-3.5" /> SKU-wise stock at this store
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left uc-label border-b border-[#EAE5D9]">
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">SKU</th>
                <th className="px-3 py-2">Batch</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2">Expiry</th>
              </tr>
            </thead>
            <tbody>
              {skuRows.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-[#8B7355]">No stock recorded yet.</td></tr>
              )}
              {skuRows.map((r, i) => (
                <tr key={i} className="border-b border-[#EAE5D9] last:border-0" data-testid={`store-sku-row-${i}`}>
                  <td className="px-3 py-2 font-medium">{r.product_name}</td>
                  <td className="px-3 py-2 text-[12px]">{r.sku}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.batch_no || "—"}</td>
                  <td className="px-3 py-2 text-right">{r.quantity}</td>
                  <td className="px-3 py-2 text-[12px]">{r.expiry_date ? fmtDate(r.expiry_date) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {eodDialogOpen && (
        <EODCloseDialog
          store={store}
          snapshot={eodSnapshot}
          onClose={() => setEodDialogOpen(false)}
          onClosed={(res) => {
            setEodDialogOpen(false);
            load();
            toast.success(`EOD ${res.number} closed. POS is now locked for today.`);
          }}
        />
      )}

      {/* Team view — HR data scoped to this store */}
      {store?.id && (
        <section className="pt-4">
          <BranchTeamPanel branchId={store.id} branchName={store.name} />
        </section>
      )}
    </div>
  );
}

const METHOD_TONE = {
  cash: "border-emerald-200 bg-emerald-50 text-emerald-800",
  gpay: "border-blue-200 bg-blue-50 text-blue-800",
  upi: "border-purple-200 bg-purple-50 text-purple-800",
  phonepe: "border-indigo-200 bg-indigo-50 text-indigo-800",
  card: "border-slate-200 bg-slate-50 text-slate-800",
  wallet: "border-amber-200 bg-amber-50 text-amber-800",
  credit: "border-red-200 bg-red-50 text-red-800",
  other: "border-[#EAE5D9] bg-[#FDFBF7] text-[#5C4033]",
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const printEodSheet = (data, store, user) => {
  const w = window.open("", "_blank", "width=820,height=900");
  if (!w) return toast.error("Pop-up blocked — allow pop-ups to print");
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const groups = (data.groups || []).map((g) => `
    <h3>${esc(g.label)} <span class="muted">· ${g.count} bill${g.count === 1 ? "" : "s"}</span></h3>
    <table>
      <thead><tr><th>#</th><th>Bill No.</th><th>Time</th><th>Customer / Ref</th><th class="r">Amount (₹)</th></tr></thead>
      <tbody>${g.bills.map((b, i) => `<tr><td>${i + 1}</td><td class="mono">${esc(b.number)}</td><td>${esc(fmtTime(b.time))}</td><td>${esc([b.customer, b.reference, b.split ? "split bill" : ""].filter(Boolean).join(" · "))}</td><td class="r mono">${Number(b.amount).toFixed(2)}</td></tr>`).join("")}</tbody>
      <tfoot><tr><td colspan="4"><b>Total ${esc(g.label)}</b></td><td class="r mono"><b>${Number(g.total).toFixed(2)}</b></td></tr></tfoot>
    </table>`).join("");
  const summary = (data.groups || []).map((g) => `<tr><td>${esc(g.label)}</td><td class="r mono">${Number(g.total).toFixed(2)}</td><td class="sig"></td></tr>`).join("");
  w.document.write(`<html><head><title>EOD Collections — ${esc(store?.code || "")} ${esc(data.business_date)}</title><style>
    @media print { @page { size: A4; margin: 12mm; } .noprint { display: none; } }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111; margin: 0; padding: 16px; }
    h1 { font-size: 18px; margin: 0; } h2 { font-size: 13px; font-weight: normal; margin: 2px 0 12px; color: #444; }
    h3 { font-size: 13px; margin: 14px 0 4px; } .muted { color: #666; font-weight: normal; }
    table { width: 100%; border-collapse: collapse; } th, td { border: 1px solid #bbb; padding: 4px 6px; text-align: left; }
    th { background: #f1ede4; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
    tfoot td { background: #faf7f0; } .r { text-align: right; } .mono { font-family: 'Courier New', monospace; }
    .sig { width: 160px; } .grand { font-size: 14px; } .box { border: 1px solid #bbb; padding: 8px; margin-top: 16px; }
    .signs { display: flex; gap: 24px; margin-top: 28px; } .signs div { flex: 1; border-top: 1px solid #333; padding-top: 4px; font-size: 11px; }
    .btn { background: #C05621; color: #fff; border: 0; padding: 8px 14px; border-radius: 6px; font-size: 13px; cursor: pointer; margin-bottom: 12px; }
  </style></head><body>
    <button class="btn noprint" onclick="window.print()">Print / Save as PDF</button>
    <h1>End of Day Collections — ${esc(store?.name || "")}</h1>
    <h2>Store ${esc(store?.code || "")} · Business date ${esc(data.business_date)} · Printed ${new Date().toLocaleString("en-IN")} by ${esc(user?.name || user?.email || "")}</h2>
    <table class="grand"><thead><tr><th>Payment method</th><th class="r">Collected (₹)</th><th>Counted / Verified</th></tr></thead>
      <tbody>${summary}</tbody>
      <tfoot><tr><td><b>Day total · ${data.bill_count} bill${data.bill_count === 1 ? "" : "s"}</b></td><td class="r mono"><b>${Number(data.grand_total).toFixed(2)}</b></td><td></td></tr></tfoot></table>
    ${groups}
    <div class="box"><b>Remarks / short-excess:</b><br/><br/><br/></div>
    <div class="signs"><div>Cashier</div><div>Showroom Manager</div><div>Verified by HO</div></div>
  </body></html>`);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 400);
};

function EodSummary({ data, store, user }) {
  if (!data) return null;
  const groups = data.groups || [];
  return (
    <div data-testid="store-eod-summary">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider text-[#8B7355] flex items-center gap-1.5">
          <IndianRupee className="w-3.5 h-3.5" /> End of Day Summary · {data.business_date} · collections by payment method
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-[#5C4033]" data-testid="store-eod-summary-total">
            {data.bill_count} bill{data.bill_count === 1 ? "" : "s"} · <span className="font-semibold">{rupee(data.grand_total)}</span> total
          </div>
          <Button size="sm" variant="outline" onClick={() => printEodSheet(data, store, user)} disabled={!data.bill_count}
                  data-testid="store-eod-print-btn" className="h-8 border-[#C05621] text-[#C05621] hover:bg-[#FDF3EC]">
            <Printer className="w-3.5 h-3.5 mr-1" /> Print / PDF
          </Button>
        </div>
      </div>
      {groups.length === 0 ? (
        <div className="bg-white border border-[#EAE5D9] rounded-lg px-3 py-6 text-center text-sm text-[#8B7355]" data-testid="store-eod-summary-empty">No completed bills yet today.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {groups.map((g) => (
            <div key={g.method} className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden" data-testid={`store-eod-group-${g.method}`}>
              <div className={`flex items-center justify-between px-3 py-2 border-b ${METHOD_TONE[g.method] || METHOD_TONE.other}`}>
                <div className="font-[Outfit] font-semibold">{g.label}</div>
                <div className="text-xs">{g.count} bill{g.count === 1 ? "" : "s"}</div>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[320px]">
                <tbody>
                  {g.bills.map((b, i) => (
                    <tr key={`${b.id}-${i}`} className="border-b border-[#EAE5D9] last:border-0" data-testid={`store-eod-bill-${g.method}-${i}`}>
                      <td className="px-3 py-1.5 font-mono text-[12px]">{b.number}</td>
                      <td className="px-3 py-1.5 text-[12px] text-[#8B7355]">{fmtTime(b.time)}{b.customer ? ` · ${b.customer}` : ""}{b.split ? " · split" : ""}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{rupee(b.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#FDFBF7] border-t border-[#EAE5D9]">
                    <td className="px-3 py-2 font-semibold" colSpan={2}>Total {g.label}</td>
                    <td className="px-3 py-2 text-right font-[Outfit] font-bold tabular-nums" data-testid={`store-eod-total-${g.method}`}>{rupee(g.total)}</td>
                  </tr>
                </tfoot>
              </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function Kpi({ label, value, icon: Icon, tone, hint, testid }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    neutral: "bg-white border-[#EAE5D9]",
  }[tone] || "bg-white border-[#EAE5D9]";
  return (
    <div className={`border rounded-lg p-3 ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-2xl font-[Outfit] font-semibold mt-0.5">{value}</div>
      {hint && <div className="text-[10px] mt-1 opacity-80">{hint}</div>}
    </div>
  );
}

function QuickCard({ to, title, desc, icon: Icon, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group">
      <div className="bg-white border border-[#EAE5D9] rounded-lg p-4 hover:border-[#C05621] hover:shadow-sm transition h-full">
        <Icon className="w-7 h-7 text-[#C05621] mb-2" />
        <div className="font-[Outfit] text-base font-semibold text-[#2C1810] group-hover:text-[#C05621]">{title}</div>
        <div className="text-xs text-[#8B7355] mt-1">{desc}</div>
        <div className="text-[11px] text-[#C05621] mt-2 flex items-center gap-1 opacity-70 group-hover:opacity-100">
          Open <ArrowRight className="w-3 h-3" />
        </div>
      </div>
    </Link>
  );
}


// ============================ EOD CLOSE DIALOG ============================
function EODCloseDialog({ store, snapshot, onClose, onClosed }) {
  const [openingCash, setOpeningCash] = useState(0);
  const [declared, setDeclared] = useState({ cash: 0, card: 0, upi: 0, wallet: 0, other: 0 });
  const [signName, setSignName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(snapshot);

  // Refresh live snapshot when dialog opens
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pos/eod/today");
        setLive(data);
      } catch {}
    })();
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const sys = live?.payments || {};
  const expectedCash = Number(openingCash || 0) + Number(sys.cash || 0);

  const variance = (m) => {
    const decv = Number(declared[m] || 0);
    const sysv = m === "cash" ? expectedCash : Number(sys[m] || 0);
    return decv - sysv;
  };
  const totalVariance = ["cash", "card", "upi", "wallet", "other"].reduce((s, m) => s + variance(m), 0);

  const submit = async () => {
    if (!signName.trim()) { toast.error("Signature name is required"); return; }
    const msg = `You are about to close ${today} for ${store?.name}.\n\n`
      + `Net sales: Rs. ${(live?.net_sales || 0).toLocaleString()}\n`
      + `Total variance: ${totalVariance > 0 ? "+" : ""}Rs. ${totalVariance.toFixed(2)}\n\n`
      + `This will LOCK the POS for the day. New sales will resume 00:00 UTC tomorrow. Continue?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pos/eod/close", {
        branch_id: store.id,
        business_date: today,
        opening_cash: Number(openingCash || 0),
        declared: {
          cash: Number(declared.cash || 0),
          card: Number(declared.card || 0),
          upi: Number(declared.upi || 0),
          wallet: Number(declared.wallet || 0),
          other: Number(declared.other || 0),
        },
        notes: notes || undefined,
        signature_name: signName.trim(),
      });
      onClosed(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close EOD");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="eod-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#2C1810]" />
            End-of-Day Close · {today}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">
            Reconcile today's sales against cash-in-drawer and card/UPI settlement. Closing will
            <b> lock the POS </b> for this store until tomorrow. A signed PDF will be available for HO.
          </DialogDescription>
        </DialogHeader>

        {/* Live snapshot */}
        <div className="bg-[#FDFBF7] border border-[#EAE5D9] rounded-md p-3">
          <div className="text-[10px] uppercase text-[#8B7355] mb-1.5">System snapshot (live)</div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <Metric label="Bills" value={live?.bill_count || 0} />
            <Metric label="Net sales" value={`₹${(live?.net_sales || 0).toLocaleString()}`} tone="emerald" />
            <Metric label="Line items" value={live?.line_items || 0} />
          </div>
        </div>

        {/* Opening cash */}
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Opening cash-in-drawer</Label>
          <Input
            type="number" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)}
            data-testid="eod-opening-cash" className="h-9"
          />
          <div className="text-[11px] text-[#8B7355] mt-1">Cash the drawer started the day with.</div>
        </div>

        {/* Reconciliation table */}
        <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uc-label border-b border-[#EAE5D9] bg-[#FDFBF7]">
                <th className="px-3 py-2">Method</th>
                <th className="px-3 py-2 text-right">System</th>
                <th className="px-3 py-2 text-right w-32">Declared *</th>
                <th className="px-3 py-2 text-right">Variance</th>
              </tr>
            </thead>
            <tbody>
              <MethodRow
                m="cash" label="Cash + opening"
                system={expectedCash}
                declared={declared.cash}
                onChange={(v) => setDeclared({ ...declared, cash: v })}
              />
              <MethodRow m="card" label="Card" system={sys.card || 0} declared={declared.card} onChange={(v) => setDeclared({ ...declared, card: v })} />
              <MethodRow m="upi" label="UPI" system={sys.upi || 0} declared={declared.upi} onChange={(v) => setDeclared({ ...declared, upi: v })} />
              <MethodRow m="wallet" label="Wallet" system={sys.wallet || 0} declared={declared.wallet} onChange={(v) => setDeclared({ ...declared, wallet: v })} />
              <MethodRow m="other" label="Other" system={sys.other || 0} declared={declared.other} onChange={(v) => setDeclared({ ...declared, other: v })} />
              <tr className="border-t-2 border-[#C05621] font-medium">
                <td className="px-3 py-2 uppercase text-[10px] tracking-wider text-[#8B7355]">Net variance</td>
                <td colSpan={2}></td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${Math.abs(totalVariance) > 0.5 ? "text-red-600" : "text-emerald-700"}`} data-testid="eod-total-variance">
                  {totalVariance > 0 ? "+" : ""}₹{totalVariance.toFixed(2)}
                </td>
              </tr>
              {sys.credit > 0 && (
                <tr className="text-[11px] text-[#8B7355]">
                  <td className="px-3 py-1 italic">Credit (unpaid — info only)</td>
                  <td className="px-3 py-1 text-right font-mono">₹{sys.credit.toFixed(2)}</td>
                  <td colSpan={2}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Signature + notes */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Signature name *</Label>
            <Input value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="eod-sig-name" placeholder="Your name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="eod-notes" placeholder="e.g. Card machine settled at 8:15pm" />
          </div>
        </div>

        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            After close, no new bills can be created at this store until midnight UTC. Only Super Admin can reopen (via database).
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit} disabled={busy || !signName.trim()}
            data-testid="eod-submit"
            className="bg-[#2C1810] hover:bg-black text-white"
          >
            <Lock className="w-4 h-4 mr-1.5" />
            {busy ? "Closing…" : "Close & Lock POS"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value, tone }) {
  const c = tone === "emerald" ? "text-emerald-700" : "text-[#2C1810]";
  return (
    <div>
      <div className="text-[10px] uppercase text-[#8B7355]">{label}</div>
      <div className={`text-lg font-semibold ${c}`}>{value}</div>
    </div>
  );
}

function MethodRow({ m, label, system, declared, onChange }) {
  const v = Number(declared || 0) - Number(system || 0);
  return (
    <tr className="border-b border-[#EAE5D9] last:border-0">
      <td className="px-3 py-1.5">{label}</td>
      <td className="px-3 py-1.5 text-right font-mono text-[13px]">₹{Number(system || 0).toFixed(2)}</td>
      <td className="px-3 py-1.5">
        <Input
          type="number"
          value={declared}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 text-sm text-right"
          data-testid={`eod-declared-${m}`}
        />
      </td>
      <td className={`px-3 py-1.5 text-right font-mono ${Math.abs(v) > 0.5 ? "text-red-600" : "text-emerald-700"}`} data-testid={`eod-variance-${m}`}>
        {v > 0 ? "+" : ""}₹{v.toFixed(2)}
      </td>
    </tr>
  );
}
